/**
 * OnboardingGate
 *
 * Invisible guard mounted inside the authenticated app shell (ProtectedLayout).
 * Redirects a brand-new user to the `/welcome` wizard exactly once the data
 * needed to make the decision is available.
 *
 * A redirect fires ONLY when all of the following hold:
 *   - the AuthContext user is loaded,
 *   - the TankContext has finished its first fetch (`isInitialized`),
 *   - that fetch did not FAIL (`error` is null),
 *   - the user has not completed (or skipped-to-complete) onboarding, and
 *   - the user owns zero tanks.
 *
 * The `isInitialized` guard prevents a first-paint false positive (tanks are
 * `[]` before the fetch resolves). `isInitialized` is deliberately true on
 * error too (so consumers never hang), which makes the separate `error` guard
 * load-bearing: on a failed fetch `tanks` is `[]` for want of data, not because
 * the user owns none, and a grandfathered user with a cold cache would
 * otherwise be redirected into the wizard. The `onboarding_completed_at` guard means we
 * never re-trap a user who finished or skipped the wizard. The `tanks.length`
 * guard grandfathers pre-feature users — they have a NULL `onboarding_completed_at`
 * but already own tanks, so they never see the wizard.
 *
 * The `/welcome` route itself is a sibling of ProtectedLayout (outside this
 * gate), so the wizard is never re-gated into a redirect loop.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTank } from '@/contexts/TankContext';

export function OnboardingGate(): null {
  const { user } = useAuth();
  const { tanks, isInitialized, error } = useTank();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || !isInitialized) return; // never decide before data is ready
    if (error) return; // failed fetch: tanks=[] means "unknown", not "none"
    if (user.onboarding_completed_at) return; // done (or skipped) — never redirect again
    if (tanks.length > 0) return; // pre-feature users: grandfather in, no wizard
    navigate('/welcome', { replace: true });
  }, [user, isInitialized, error, tanks.length, navigate]);

  return null;
}
